import { useJobillaOrgId } from '@/hooks'
import { useAuth } from '@/lib'
import { Button, SubtleLoader } from '@/ui'
import { WidgetInputToggleField } from '@/ui/rapid-ui/inputs/widgets/widget-input-toggle-field'
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import {
	invoiceEmailSettingsShowQueryKey,
	useInvoiceEmailSettingsShow,
	useInvoiceEmailSettingsUpdate,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

type InvoiceEmailSettingsDialogProps = {
	open: boolean
	onClose: () => void
}

type EmailSettingsForm = {
	sendInvoiceEmails: boolean
	sendReceiptEmails: boolean
	sendInvoiceForZeroEuro: boolean
	sendInvoiceForFullyPaidByWallet: boolean
	sendReceiptForWalletPayments: boolean
	sendInvoiceForFullyPaidAtIssue: boolean
}

type SettingKey = keyof EmailSettingsForm

const SETTING_KEYS: SettingKey[] = [
	'sendInvoiceEmails',
	'sendReceiptEmails',
	'sendInvoiceForZeroEuro',
	'sendInvoiceForFullyPaidByWallet',
	'sendReceiptForWalletPayments',
	'sendInvoiceForFullyPaidAtIssue',
]

export function InvoiceEmailSettingsDialog({ open, onClose }: InvoiceEmailSettingsDialogProps) {
	const { t } = useTranslation('common')
	const { can } = useAuth()
	const organisationId = useJobillaOrgId()
	const queryClient = useQueryClient()
	const canManage = can('billing:manage')

	const { data } = useInvoiceEmailSettingsShow(organisationId, {
		enabled: open && !!organisationId,
	})

	const [form, setForm] = useState<EmailSettingsForm | null>(null)
	const [hasError, setHasError] = useState(false)

	// Seed the form once when the dialog opens with data, then detach so a
	// background refetch can't clobber the user's edits. Reset on close.
	useEffect(() => {
		if (!open) {
			setForm(null)
			setHasError(false)
			return
		}
		if (data && form === null) {
			setForm({
				sendInvoiceEmails: data.sendInvoiceEmails,
				sendReceiptEmails: data.sendReceiptEmails,
				sendInvoiceForZeroEuro: data.sendInvoiceForZeroEuro,
				sendInvoiceForFullyPaidByWallet: data.sendInvoiceForFullyPaidByWallet,
				sendReceiptForWalletPayments: data.sendReceiptForWalletPayments,
				sendInvoiceForFullyPaidAtIssue: data.sendInvoiceForFullyPaidAtIssue,
			})
		}
	}, [open, data, form])

	const saveMutation = useInvoiceEmailSettingsUpdate({
		onSuccess: (result) => {
			queryClient.setQueryData(invoiceEmailSettingsShowQueryKey(organisationId), result)
			onClose()
		},
		onError: () => setHasError(true),
	})

	const isDirty = !!form && !!data && SETTING_KEYS.some((key) => form[key] !== data[key])

	const sections = [
		{
			key: 'sendInvoiceEmails' as const,
			title: t('settings.invoices.emails.invoice.title'),
			description: t('settings.invoices.emails.invoice.description'),
			options: [
				{
					key: 'sendInvoiceForZeroEuro' as const,
					title: t('settings.invoices.emails.invoice.zeroEuro.title'),
					description: t('settings.invoices.emails.invoice.zeroEuro.description'),
				},
				{
					key: 'sendInvoiceForFullyPaidByWallet' as const,
					title: t('settings.invoices.emails.invoice.fullyPaidByWallet.title'),
					description: t('settings.invoices.emails.invoice.fullyPaidByWallet.description'),
				},
				{
					key: 'sendInvoiceForFullyPaidAtIssue' as const,
					title: t('settings.invoices.emails.invoice.fullyPaidAtIssue.title'),
					description: t('settings.invoices.emails.invoice.fullyPaidAtIssue.description'),
				},
			],
		},
		{
			key: 'sendReceiptEmails' as const,
			title: t('settings.invoices.emails.receipt.title'),
			description: t('settings.invoices.emails.receipt.description'),
			options: [
				{
					key: 'sendReceiptForWalletPayments' as const,
					title: t('settings.invoices.emails.receipt.walletPayments.title'),
					description: t('settings.invoices.emails.receipt.walletPayments.description'),
				},
			],
		},
	]

	const setValue = (key: SettingKey, value: boolean) => setForm((prev) => prev && { ...prev, [key]: value })

	const save = () => {
		if (!form) { return }
		setHasError(false)
		saveMutation.mutate({ organisation: organisationId, data: form })
	}

	return (
		<AnimatePresence>
			{open && (
				<Dialog static open={open} onClose={onClose} className="relative z-50">
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="fixed inset-0 bg-black/30 backdrop-blur-sm"
					/>

					<div className="fixed inset-0 flex items-center justify-center p-4">
						<DialogPanel
							as={motion.div}
							initial={{ opacity: 0, scale: 0.95 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0, scale: 0.95 }}
							className="bg-white rounded-lg shadow-xl max-w-lg w-full overflow-hidden"
						>
							<div className="p-6">
								<DialogTitle className="text-lg font-semibold text-gray-900">
									{t('settings.invoices.emails.title')}
								</DialogTitle>
								<p className="mt-1 text-sm text-grey-600">
									{t('settings.invoices.emails.description')}
								</p>

								<div className="mt-5 max-h-[60vh] overflow-y-auto">
									{!form
										? <SubtleLoader delay={0} />
										: (
											<div className="space-y-4">
												{sections.map((section) => {
													const sectionEnabled = form[section.key]
													return (
														<div
															key={section.key}
															className="overflow-hidden rounded-lg border border-grey-200"
														>
															<div className="flex items-center justify-between gap-4 p-4">
																<div className="max-w-xs space-y-1">
																	<div className="font-medium text-grey-900">
																		{section.title}
																	</div>
																	<div className="text-sm text-grey-600">
																		{section.description}
																	</div>
																</div>
																<WidgetInputToggleField
																	value={sectionEnabled}
																	onChange={(value) => setValue(section.key, value)}
																	disabled={!canManage || saveMutation.isPending}
																	ariaLabel={section.title}
																/>
															</div>
															{section.options.length > 0 && (
																<div className="divide-y divide-grey-100 border-t border-grey-100 bg-grey-50">
																	{section.options.map((option) => (
																		<div
																			key={option.key}
																			className="flex items-center justify-between gap-4 py-3 pl-6 pr-4"
																		>
																			<div className="max-w-xs space-y-1">
																				<div className="text-sm font-medium text-grey-800">
																					{option.title}
																				</div>
																				<div className="text-sm text-grey-500">
																					{option.description}
																				</div>
																			</div>
																			<WidgetInputToggleField
																				value={form[option.key]}
																				onChange={(value) =>
																					setValue(option.key, value)}
																				disabled={!canManage
																					|| saveMutation.isPending
																					|| !sectionEnabled}
																				ariaLabel={option.title}
																			/>
																		</div>
																	))}
																</div>
															)}
														</div>
													)
												})}
											</div>
										)}

									{hasError && (
										<p className="mt-3 text-sm text-red-600">
											{t('settings.invoices.emails.error')}
										</p>
									)}
								</div>
							</div>

							<div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
								<Button type="subtle" onClick={onClose} disabled={saveMutation.isPending}>
									{t('common.cancel')}
								</Button>
								{canManage && (
									<Button
										type="committal"
										onClick={save}
										isLoading={saveMutation.isPending}
										disabled={!isDirty || saveMutation.isPending}
									>
										{t('common.save')}
									</Button>
								)}
							</div>
						</DialogPanel>
					</div>
				</Dialog>
			)}
		</AnimatePresence>
	)
}
